/**
 * `mod.window`: the content tree, named as an app sees it (RFC 0284 §4c, S2).
 *
 * Every method here exists because the built-in sidebar calls it today. That is
 * the whole admission test, and it is why the surface is six functions rather
 * than a general tree API: a method no shipped chrome needs is speculation, and
 * a capability full of speculation is one nobody can review.
 *
 * PURE, AND THAT IS THE POINT. `makeWindow` takes the tree and a writer and
 * returns the capability; it holds no React state and touches no document. The
 * shell owns the state and hands it in, which is what lets every rule below be
 * tested against a plain tree rather than against a mounted window.
 *
 * AN APP PROPOSES, IT NEVER SUPPLIES A TREE (§4c invariant). Each method names
 * a subject and a position, runs a `contentTree` operation, and ends in
 * `normalize`. There is no path by which a caller hands back a node, so RFC
 * 0266's four rules hold by construction rather than by trusting whoever is on
 * the other side of the bridge.
 */

import {
  type Content,
  type ContentNode,
  closeLeaf,
  type DropEdge,
  leaves,
  normalize,
  openLeaf,
} from '../features/code/contentTree'

/** What a pane looks like from outside: a projection, never the node. */
export interface LeafView {
  leafId: string
  content: Content
  focused: boolean
}

/** What the window is showing right now. */
export interface WindowView {
  leaves: LeafView[]
  focused: string | null
  space: string | null
}

/**
 * What an app may ask to open.
 *
 * A CLOSED SET of two kinds, not `Content`. `Content` includes `picker` and
 * `terminal`: a picker has no subject to name, and a terminal is a live
 * connection to a machine rather than something an app can conjure by id.
 * Widening this later is a decision; leaving it open would have been an
 * accident.
 */
export interface WindowSubject {
  kind: 'document' | 'agent'
  id: string | null
  projectId: string | null
}

/** Where a subject lands. Absent means the focused pane, which is a plain
 *  click; an edge is the drag that splits. */
export interface WindowWhere {
  leafId?: string
  edge?: 'left' | 'right' | 'up' | 'down'
}

export interface WindowCapability {
  read(): WindowView
  /** Returns the id of the leaf it opened into. */
  open(subject: WindowSubject, where?: WindowWhere): string | null
  close(leafId: string): void
  focus(leafId: string): void
  setSpace(spaceId: string): void
  onChange(fn: () => void): () => void
}

/** What the shell hands in: its state, and the four ways to change it. */
export interface WindowDeps {
  tree: () => ContentNode | null
  focused: () => string | null
  space: () => string | null
  writeTree: (next: ContentNode | null) => void
  setFocus: (leafId: string) => void
  /** Unknown ids are IGNORED rather than guessed at, per §4c. */
  switchSpace: (spaceId: string) => void
  subscribe: (fn: () => void) => () => void
}

/**
 * The edge names an app uses, mapped to the tree's.
 *
 * `up`/`down` outside and `top`/`bottom` inside. Not worth a rename in either
 * direction: the tree's names are what every drop target in the shell already
 * says, and an app author writing `up` is following the arrow keys. One table
 * is cheaper than either migration.
 */
const EDGES: Record<string, DropEdge> = {
  left: 'left',
  right: 'right',
  up: 'top',
  down: 'bottom',
}

/**
 * Turn a subject into leaf content, or null when it cannot be one.
 *
 * A DOCUMENT NEEDS AN ID and a project; an AGENT needs neither, because a
 * session leaf with no session is the session list, which is a real thing to
 * open. That asymmetry is the reason this is a function rather than a cast.
 */
function contentFor(subject: WindowSubject): Content | null {
  if (subject.kind === 'document') {
    if (!subject.id || !subject.projectId) return null
    return { kind: 'document', projectId: subject.projectId, docId: subject.id }
  }
  return {
    kind: 'agent',
    projectId: subject.projectId,
    sessionId: subject.id,
  } as Content
}

export function makeWindow(deps: WindowDeps): WindowCapability {
  return {
    read() {
      const focused = deps.focused()
      return {
        leaves: leaves(deps.tree()).map((l) => ({
          leafId: l.id,
          content: l.content,
          focused: l.id === focused,
        })),
        focused,
        space: deps.space(),
      }
    },

    open(subject, where) {
      const content = contentFor(subject)
      // REFUSED, not approximated. An open that cannot be honoured returns null
      // and moves nothing, because the alternative is a pane showing something
      // the caller did not ask for and cannot tell apart from success.
      if (!content) return null
      const target = where?.leafId ?? deps.focused()
      /**
       * AN UNKNOWN EDGE IS A REFUSAL, not a centre.
       *
       * `EDGES` maps four names; anything else came back undefined and fell
       * through to 'center', which REPLACES the target pane's content. So a
       * fill with a typo — `edge: 'sideways'` — silently destroyed
       * what was in the focused pane instead of splitting beside it, and got a
       * leaf id back saying it had worked. Same rule as `contentFor` below:
       * refuse rather than approximate.
       */
      let edge: DropEdge = 'center'
      if (where?.edge !== undefined) {
        const known = EDGES[where.edge]
        if (!known) return null
        edge = known
      }
      const before = deps.tree()
      const next = openLeaf(before, content, target, edge)
      const normalized = normalize(next)
      deps.writeTree(normalized)
      // The leaf the tree actually minted, found by identity against what was
      // there before. Returning a guessed id would be a handle to nothing.
      const had = new Set(leaves(before).map((l) => l.id))
      return leaves(normalized).find((l) => !had.has(l.id))?.id ?? null
    },

    close(leafId) {
      deps.writeTree(normalize(closeLeaf(deps.tree(), leafId)))
    },

    focus(leafId) {
      // Only a leaf that EXISTS. Focusing a stale id would leave the window
      // with a focus nothing renders, and every later open would land nowhere.
      if (leaves(deps.tree()).some((l) => l.id === leafId))
        deps.setFocus(leafId)
    },

    setSpace(spaceId) {
      deps.switchSpace(spaceId)
    },

    onChange(fn) {
      return deps.subscribe(fn)
    },
  }
}
